import React, { useState, useEffect } from "react";
import { getLastLoginDetails } from "../../services/dataService";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Breadcrumb from "../../components/common/Breadcrumb";
import { getSecureItem } from "../../services/SecureStorage";

function LoginDetails() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loginData, setLoginData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("Web");
  const [searchTerm, setSearchTerm] = useState("");
  const UID = getSecureItem("userId");

  const fetchLoginDetails = async () => {
    if (!fromDate || !toDate) {
      alert("Please select both dates");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      alert("From date cannot be later than To date");
      return;
    }

    try {
      setLoading(true);
      const Data = { fromDate, toDate, UserId: UID };
      const result = await getLastLoginDetails(Data);
      const allData = result?.data?.data || [];
      console.log("API Response:", allData); // Log for debugging
      setLoginData(allData);
      applyFilter(allData, filterType, searchTerm);
    } catch (error) {
      console.error("Error fetching login details:", error);
      alert("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (data, type, search = "") => {
    let filtered = [];
    if (type === "Mobile") {
      filtered = data.filter(
        (item) => item.EmployeeCode !== null || item.EmployeeName !== null
      );
    } else if (type === "Web") {
      filtered = data.filter((item) => item.UserName !== null);
    }

    if (search.trim() !== "") {
      const term = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.EmployeeName &&
            item.EmployeeName.toLowerCase().includes(term)) ||
          (item.EmployeeCode &&
            item.EmployeeCode.toLowerCase().includes(term)) ||
          (item.UserName && item.UserName.toLowerCase().includes(term))
      );
    }

    setFilteredData(filtered);
  };

  const handleFilterChange = (e) => {
    const type = e.target.value;
    setFilterType(type);
    applyFilter(loginData, type, searchTerm);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    applyFilter(loginData, filterType, value);
  };

  const exportPDF = () => {
    if (filteredData.length === 0) {
      alert("No data available to export.");
      return;
    }

    const doc = new jsPDF();

    const title = `LOGIN DETAILS REPORT - ${filterType.toUpperCase()} USERS`;
    const dateRange = `FROM ${new Date(
      fromDate
    ).toLocaleDateString("en-IN")} TO ${new Date(toDate).toLocaleDateString(
      "en-IN"
    )}`;

    doc.text(title, 105, 15, { align: "center" });
    doc.text(dateRange, 105, 25, { align: "center" });

    const tableHead =
      filterType === "Mobile"
        ? [["EMPLOYEE CODE", "EMPLOYEE NAME", "LAST LOGIN"]]
        : [["USER NAME", "LAST LOGIN"]];

    const tableBody = filteredData.map((item) =>
      filterType === "Mobile"
        ? [
            item.EmployeeCode || "-",
            item.EmployeeName || "-",
            new Date(item.LastLogin).toLocaleDateString("en-IN") +
              " " +
              new Date(item.LastLogin.replace("Z", "")).toLocaleTimeString(
                "en-IN",
                { hour12: false }
              ),
          ]
        : [
            item.UserName || "-",
            new Date(item.LastLogin).toLocaleDateString("en-IN") +
              " " +
              new Date(item.LastLogin.replace("Z", "")).toLocaleTimeString(
                "en-IN",
                { hour12: false }
              ),
          ]
    );

    doc.autoTable({
      head: tableHead,
      body: tableBody,
      startY: 35,
      theme: "grid",
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        fontSize: 10,
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        halign: "center",
        valign: "middle",
        fontSize: 9,
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
      },
      tableLineColor: [0, 0, 0],
      tableLineWidth: 0.5,
      margin: { top: 35, left: 20, right: 20 },
    });

    const currentDate = new Date().toISOString().split("T")[0];
    doc.save(`LoginDetails_${filterType}_${currentDate}.pdf`);
  };

  useEffect(() => {
    if (loginData.length > 0) {
      applyFilter(loginData, filterType, searchTerm);
    }
  }, [loginData]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <Breadcrumb title="Login Details" />
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col min-w-[140px]">
              <label className="text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex flex-col min-w-[140px]">
              <label className="text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={fetchLoginDetails}
              disabled={loading}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                loading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-blue text-white hover:bg-blue focus:outline-none focus:ring-2 focus:ring-blue"
              }`}
            >
              {loading ? "Loading..." : "Get Details"}
            </button>
            <div className="flex flex-col min-w-[160px]">
              <label className="text-sm font-medium text-gray-700 mb-1">
                User Type
              </label>
              <select
                value={filterType}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="Mobile">Mobile APP Users</option>
                <option value="Web">Web Site Users</option>
              </select>
            </div>
            <div className="flex flex-col min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={exportPDF}
              disabled={filteredData.length === 0}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                filteredData.length === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              }`}
            >
              Export PDF
            </button>
          </div>
        </div>
        {filteredData.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {filterType} Users Login Details
                  </h2>
                  <p className="text-sm text-gray-600">
                    Showing {filteredData.length} records
                    {searchTerm && ` matching "${searchTerm}"`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Date Range</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(fromDate).toLocaleDateString()} -{" "}
                    {new Date(toDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      #
                    </th>
                    {filterType === "Mobile" && (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee Name
                        </th>
                      </>
                    )}
                    {filterType === "Web" && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User Name
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {index + 1}
                      </td>
                      {filterType === "Mobile" && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="">
                              {item.EmployeeCode || "-"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.EmployeeName || "-"}
                          </td>
                        </>
                      )}
                      {filterType === "Web" && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.UserName || "-"}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {new Date(item.LastLogin).toLocaleDateString(
                              "en-IN"
                            )}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(
                              item.LastLogin.replace("Z", "")
                            ).toLocaleTimeString("en-IN", {
                              hour12: false,
                            })}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {filteredData.length === 0 && !loading && loginData.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Results Found
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm
                ? `No records match your search "${searchTerm}"`
                : `No ${filterType.toLowerCase()} users found for the selected date range`}
            </p>
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  applyFilter(loginData, filterType, "");
                }}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear search
              </button>
            )}
          </div>
        )}
        {loginData.length === 0 && !loading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Data Available
            </h3>
            <p className="text-gray-500">
              Select a date range and click "Get Details" to view login records.
            </p>
          </div>
        )}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Loading...
            </h3>
            <p className="text-gray-500">
              Fetching login details, please wait.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginDetails;